import { Button, Spinner } from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, { CautionFilledIcon } from '@enterprise-ui/icons'
import { TloAlert } from 'common/types'
import { TLO_SINGLE_TRUCK_LIMITS } from 'services/tloGroupSummary'

type Props = {
  alerts: TloAlert[]
  onViewTlo: (tloRefId: string) => void
  isLoading?: boolean
}

const formatNumber = (value: number) => value.toLocaleString()

export const TloWarnings = ({
  alerts,
  onViewTlo,
  isLoading = false,
}: Props) => {
  const warnings = alerts.filter((alert) => alert.summary)

  if (!warnings.length && !isLoading) return null

  return (
    <div data-testid="tlo-warnings">
      {isLoading && (
        <div
          data-testid="tlo-warnings-loading"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '28px 0 16px',
          }}
        >
          <Spinner size="inline" />
          Checking TLO shipments for warnings...
        </div>
      )}
      {warnings.flatMap((alert) => {
        const summary = alert.summary
        if (!summary) return []

        const warningElements = []

        if (summary.isOverCapacity) {
          warningElements.push(
            <div
              key={`${summary.tloRefId}-capacity`}
              data-testid="tlo-capacity-warning"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                margin: '28px 0 16px',
                border: '2px solid #c62828',
                borderRadius: '4px',
                backgroundColor: '#fff0f0',
                color: '#8f1d1d',
              }}
            >
              <EnterpriseIcon
                icon={CautionFilledIcon}
                size="xl"
                style={{ color: '#c62828', flexShrink: 0 }}
                aria-label="Capacity warning"
              />
              <div style={{ flex: 1 }}>
                <strong>TLO group exceeds single truck capacity</strong>
                <div>
                  All shipments in TLO {summary.tloRefId} total more than what
                  fits on one truck. Adjust shipments so totals are less than{' '}
                  {formatNumber(TLO_SINGLE_TRUCK_LIMITS.weight)} lbs,{' '}
                  {formatNumber(TLO_SINGLE_TRUCK_LIMITS.cube)} ft³, and{' '}
                  {TLO_SINGLE_TRUCK_LIMITS.palletSpaces} pallet spaces.
                </div>
              </div>
              <Button
                type="secondary"
                onClick={() => onViewTlo(summary.tloRefId)}
              >
                View TLO shipments and totals
              </Button>
            </div>,
          )
        }

        if (alert.isIncomplete) {
          warningElements.push(
            <div
              key={`${summary.tloRefId}-incomplete`}
              data-testid="tlo-incomplete-warning"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                margin: '28px 0 16px',
                border: '2px solid #f2b01e',
                borderRadius: '4px',
                backgroundColor: '#fffde7',
                color: '#6b4f00',
              }}
            >
              <EnterpriseIcon
                icon={CautionFilledIcon}
                size="xl"
                style={{ color: '#f2b01e', flexShrink: 0 }}
                aria-label="TLO shipments incomplete warning"
              />
              <div style={{ flex: 1 }}>
                <strong>TLO Shipments Incomplete</strong>
                <div>{alert.incompleteMessage}</div>
              </div>
              <Button
                type="secondary"
                onClick={() => onViewTlo(summary.tloRefId)}
              >
                View TLO shipments and totals
              </Button>
            </div>,
          )
        }

        return warningElements
      })}
    </div>
  )
}
